import { useEffect, useState, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export function NotificationBell() {
  const { t, L } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative h-10 w-10 flex items-center justify-center transition-colors"
        style={{ borderRadius: "var(--radius)", color: "var(--text)" }}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 text-[9px] font-bold flex items-center justify-center rounded-full"
            style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 z-50 border shadow-xl kw-fade overflow-hidden"
          style={{ background: "var(--surface)", borderColor: "var(--border)", borderRadius: "var(--radius)" }}
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <span className="font-semibold text-sm" style={{ color: "var(--text)" }}>
              {t("dash.notifications")}
            </span>
            <button onClick={readAll} className="text-xs inline-flex items-center gap-1" style={{ color: "var(--accent)" }} data-testid="notifications-read-all">
              <Check className="h-3 w-3" /> {t("common.readAll")}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                {t("common.none")}
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                className="px-4 py-3 border-b text-sm"
                style={{ borderColor: "var(--border)", background: n.read ? "transparent" : "var(--surface-alt)" }}
              >
                <div className="font-medium" style={{ color: "var(--text)" }}>{L(n.title)}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{L(n.body)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
